/**
 * Rendering of the Markdown dialect: core's AST → React elements. Never innerHTML; every link
 * target was checked by the parser (safeUrl), and React escapes all text and attributes (SPEC §14).
 *
 * Refs resolve live through the title index, so a link turns from "missing" to resolved the moment
 * its page exists, on any device. Text runs carry their source offset (`data-o`), so a click on
 * rendered text puts the caret at the same place in the raw text.
 */
import { Square, SquareCheck, SquareDot, SquareX } from 'lucide-react'
import { memo, useSyncExternalStore, type MouseEvent, type ReactNode } from 'react'
import { parse, plainText, type Block, type Inline, type NodeId, type RefKind, type TaskMarker } from '@solar/core'
import { useNode, useStore } from './hooks.ts'
import { navigate } from './router.ts'

const MAX_DEPTH = 2

export const Content = memo(function Content({ content, depth = 0, nodeId }: { content: string; depth?: number; nodeId?: NodeId }) {
  if (content === '') return <span className="placeholder">{'​'}</span>
  const doc = parse(content)
  const task = doc.task
  const blocks = doc.blocks
  // The common case, one paragraph: inline content directly, no wrapper element.
  const body =
    blocks.length === 1 && blocks[0]!.t === 'p' ? inlines(blocks[0]!.c, depth, 'p') : blocks.map((b, i) => block(b, depth, `b${i}`))
  if (!task) return <>{body}</>
  return (
    <>
      <TaskBox task={task} nodeId={nodeId} />
      <span className={task === 'DONE' || task === 'CANCELLED' ? 'done-text' : undefined}>{body}</span>
    </>
  )
})

const TASK_ICON = { TODO: Square, DOING: SquareDot, DONE: SquareCheck, CANCELLED: SquareX }

/** The task checkbox: a click marks done (or reopens); ⌘⏎ cycles through every status. */
function TaskBox({ task, nodeId }: { task: TaskMarker; nodeId?: NodeId }) {
  const store = useStore()
  const Icon = TASK_ICON[task]
  return (
    <span
      className={`check ${task.toLowerCase()}`}
      role="checkbox"
      aria-checked={task === 'DONE'}
      aria-label={task}
      title={task === 'DONE' ? 'Done. Click to reopen' : 'Click to mark done'}
      onMouseDown={(e) => {
        if (!nodeId) return
        e.preventDefault()
        e.stopPropagation()
        store.toggleTask(nodeId)
      }}
    >
      <Icon size={15} aria-hidden />
    </span>
  )
}

function block(b: Block, depth: number, key: string): ReactNode {
  switch (b.t) {
    case 'p':
      return (
        <div key={key} className="md-p">
          {inlines(b.c, depth, key)}
        </div>
      )
    case 'h':
      return (
        <div key={key} className={`h h${b.level}`} role="heading" aria-level={b.level}>
          {inlines(b.c, depth, key)}
        </div>
      )
    case 'quote':
      return (
        <blockquote key={key} className="quote">
          {b.lines.map((l, i) => (
            <div key={i}>{inlines(l, depth, `${key}.${i}`)}</div>
          ))}
        </blockquote>
      )
    case 'pre':
      return (
        <pre key={key} className="codeblock" data-lang={b.lang || undefined}>
          <code>{b.v}</code>
        </pre>
      )
    case 'hr':
      return <hr key={key} />
  }
}

function inlines(xs: readonly Inline[], depth: number, key: string): ReactNode[] {
  return xs.map((x, i) => inline(x, depth, `${key}.${i}`))
}

function inline(x: Inline, depth: number, key: string): ReactNode {
  switch (x.t) {
    case 'text':
      return (
        <span key={key} data-o={x.from}>
          {x.v}
        </span>
      )
    case 'br':
      return <br key={key} />
    case 'code':
      return <code key={key}>{x.v}</code>
    case 'strong':
      return <strong key={key}>{inlines(x.c, depth, key)}</strong>
    case 'em':
      return <em key={key}>{inlines(x.c, depth, key)}</em>
    case 'del':
      return <s key={key}>{inlines(x.c, depth, key)}</s>
    case 'mark':
      return <mark key={key}>{inlines(x.c, depth, key)}</mark>
    case 'link':
      return (
        <a key={key} className="ext" href={x.href} target="_blank" rel="noopener noreferrer nofollow">
          {inlines(x.c, depth, key)}
        </a>
      )
    case 'url':
      return (
        <a key={key} className="ext" href={x.href} target="_blank" rel="noopener noreferrer nofollow">
          {x.v}
        </a>
      )
    case 'image':
      return <img key={key} className="img" src={x.src} alt={x.alt} loading="lazy" referrerPolicy="no-referrer" />
    case 'ref':
      return <Ref key={key} kind={x.kind} title={x.title} />
    case 'blockref':
      return <BlockRef key={key} id={x.id} depth={depth} />
    case 'embed':
      return <Embed key={key} id={x.id} title={x.title} depth={depth} />
    case 'asset':
      return (
        <span key={key} className="chip muted">
          attachment
        </span>
      )
    case 'segref':
      return (
        <span key={key} className="chip muted">
          transcript quote
        </span>
      )
  }
}

/** Opening a link; ⌘/⇧-click opens it in the right sidebar instead (SPEC §6.2). */
function follow(e: MouseEvent, go: (inSidebar: boolean) => void): void {
  e.preventDefault()
  e.stopPropagation()
  go(e.metaKey || e.ctrlKey || e.shiftKey)
}

function useResolved(title: string | null): NodeId | null {
  const store = useStore()
  const get = (): NodeId | null => (title === null ? null : store.titles.resolve(title))
  return useSyncExternalStore(store.titles.subscribe, get, get)
}

const Ref = memo(function Ref({ kind, title }: { kind: RefKind; title: string }) {
  const store = useStore()
  const id = useResolved(title)
  const cls = (kind === 'tag' ? 'tag' : kind === 'mention' ? 'mention' : 'ref') + (id ? '' : ' missing')
  return (
    <a
      className={cls}
      href={id ? `/n/${id}` : undefined}
      data-ref={title}
      title={id ? undefined : `“${title}” does not exist yet. Click to create it.`}
      onClick={(e) => follow(e, (side) => store.openTitle(title, side))}
    >
      {kind === 'tag' ? `#${title}` : kind === 'mention' ? `@${title}` : (
        <>
          <span className="br">[[</span>
          {title}
          <span className="br">]]</span>
        </>
      )}
    </a>
  )
})

const BlockRef = memo(function BlockRef({ id, depth }: { id: NodeId; depth: number }) {
  const store = useStore()
  const n = useNode(id)
  if (!n || n.deletedAt !== null) return <span className="blockref missing">((missing block))</span>
  return (
    <a
      className="blockref"
      href={`/n/${id}`}
      data-block={id}
      onClick={(e) => follow(e, (side) => (side ? store.openInSidebar({ kind: 'node', id }) : navigate(`/n/${id}`)))}
    >
      {depth >= MAX_DEPTH ? plainText(n.content) : <Content content={n.content} depth={depth + 1} />}
    </a>
  )
})

const Embed = memo(function Embed({ id, title, depth }: { id: NodeId | null; title: string | null; depth: number }) {
  const store = useStore()
  const resolved = useResolved(title)
  const target = id ?? resolved
  const n = useNode(target)
  if (!n || n.deletedAt !== null) return <span className="embed missing">{title ? `[[${title}]]` : '((missing block))'} not found</span>
  return (
    <span className="embed" onMouseDown={(e) => e.stopPropagation()}>
      <a className="embed-head" href={`/n/${n.id}`} onClick={(e) => follow(e, (side) => (side ? store.openInSidebar({ kind: 'node', id: n.id }) : navigate(`/n/${n.id}`)))}>
        {n.title ?? plainText(n.content)}
      </a>
      {depth < MAX_DEPTH && <EmbedChildren id={n.id} depth={depth + 1} />}
    </span>
  )
})

function EmbedChildren({ id, depth }: { id: NodeId; depth: number }) {
  const store = useStore()
  const kids = store.g.children(id).filter((k) => store.g.isLive(k)).slice(0, 50)
  if (kids.length === 0) return null
  return (
    <span className="embed-kids">
      {kids.map((k) => (
        <EmbedRow key={k} id={k} depth={depth} />
      ))}
    </span>
  )
}

function EmbedRow({ id, depth }: { id: NodeId; depth: number }) {
  const n = useNode(id)
  if (!n) return null
  return (
    <span className="embed-row">
      <span className="dot" />
      <span>
        <Content content={n.content} depth={depth} />
        {!n.collapsed && depth < MAX_DEPTH && <EmbedChildren id={id} depth={depth + 1} />}
      </span>
    </span>
  )
}

/** The raw-text offset under a click on rendered text, or null (then the caret goes to the end). */
export function sourceOffsetAt(x: number, y: number): number | null {
  const doc = document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null
    caretRangeFromPoint?: (x: number, y: number) => Range | null
  }
  let node: Node | null = null
  let offset = 0
  if (doc.caretPositionFromPoint) {
    const p = doc.caretPositionFromPoint(x, y)
    if (p) ({ offsetNode: node, offset } = p)
  } else if (doc.caretRangeFromPoint) {
    const r = doc.caretRangeFromPoint(x, y)
    if (r) ({ startContainer: node, startOffset: offset } = r)
  }
  if (!node || node.nodeType !== Node.TEXT_NODE) return null
  const el = node.parentElement
  if (!el || !el.hasAttribute('data-o') || el.firstChild !== node) return null
  return Number(el.getAttribute('data-o')) + offset
}
